import React, { useState } from "react";
import { Row, Col, Button, TextLink, MessageBanner, } from "@folio/stripes/components";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useFieldArrayErrors } from "../../hooks/useFieldArrayErrors";
import { FRONT_COVER_TYPE_VALUE, IMAGE_MODE_VALUE, TABLE_OF_CONTENTS_TYPE_VALUE, TEXT_MODE_VALUE, } from "../../utils/resourceImageRules";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { useItemEditor } from "../../hooks/title/useItemEditor";
import { useContentRows } from "../../utils/toContentRows";
import { ResourceFormModal } from "./ResourceFormModal";
import { EntityRowList } from "./EntityRowList";
import css from "./FormModal.module.css";
var GENERIC_ADD_TITLE_ID = "ui-br-agency.title.resources.form.add.title";
/** Codetable item with the given ONIX value — never a hardcoded id. */
var onixByValue = function (table, value) { var _a; return (_a = table === null || table === void 0 ? void 0 : table.onixCodetableItems) === null || _a === void 0 ? void 0 : _a.find(function (item) { return item.value === value; }); };
/**
 * The add buttons of the "Cover and contents" tab (issue #75957): each one
 * opens the resource form pre-filled with its ONIX type/mode and titled after
 * the button. The generic button leaves the type empty.
 */
var ADD_BUTTONS = [
    {
        labelId: "ui-br-agency.title.form.add.frontCover",
        modalTitleId: "ui-br-agency.title.form.add.frontCover",
        typeValue: FRONT_COVER_TYPE_VALUE,
        modeValue: IMAGE_MODE_VALUE,
    },
    {
        labelId: "ui-br-agency.title.form.add.tableOfContents",
        modalTitleId: "ui-br-agency.title.form.add.tableOfContents",
        typeValue: TABLE_OF_CONTENTS_TYPE_VALUE,
        modeValue: TEXT_MODE_VALUE,
    },
    {
        labelId: "ui-br-agency.title.form.add.resource",
        modalTitleId: GENERIC_ADD_TITLE_ID,
        modeValue: IMAGE_MODE_VALUE,
    },
];
var TitleSupportingResourcesForm = function () {
    var _a, _b;
    var formatMessage = useIntl().formatMessage;
    var _c = useFormContext(), control = _c.control, getValues = _c.getValues;
    var errorMessages = useFieldArrayErrors("supportingResources");
    var _d = useFieldArray({ control: control, name: "supportingResources" }), resourceFields = _d.fields, appendResource = _d.append, updateResource = _d.update, removeResource = _d.remove;
    var _e = useOnixOptions(), resourceTypeTable = _e.resourceTypeTable, resourceTypeLoading = _e.resourceTypeLoading, resourceTypeStatus = _e.resourceTypeStatus, resourceModeTable = _e.resourceModeTable, resourceModeLoading = _e.resourceModeLoading, resourceModeStatus = _e.resourceModeStatus;
    var _f = useItemEditor({
        appendItem: appendResource,
        updateItem: updateResource,
        createEmpty: function () { return ({ link: "" }); },
    }), isOpen = _f.isOpen, isEditing = _f.isEditing, draft = _f.draft, setDraft = _f.setDraft, openEditor = _f.openEditor, openNew = _f.openNew, closeEditor = _f.closeEditor, saveItem = _f.saveItem;
    var _g = useState(GENERIC_ADD_TITLE_ID), addTitleId = _g[0], setAddTitleId = _g[1];
    // A button stays disabled until the codetables are loaded and contain the
    // items it seeds — otherwise the form would open titled "Přidat obsah" with
    // an empty type and mode.
    var codetablesLoading = resourceTypeLoading || resourceModeLoading;
    var isAddReady = function (button) {
        var _a;
        return !codetablesLoading &&
            !!((_a = resourceTypeTable === null || resourceTypeTable === void 0 ? void 0 : resourceTypeTable.onixCodetableItems) === null || _a === void 0 ? void 0 : _a.length) &&
            !!onixByValue(resourceModeTable, button.modeValue) &&
            (!button.typeValue || !!onixByValue(resourceTypeTable, button.typeValue));
    };
    var openAdd = function (button) {
        setAddTitleId(button.modalTitleId);
        openNew({
            type: button.typeValue
                ? onixByValue(resourceTypeTable, button.typeValue)
                : undefined,
            mode: onixByValue(resourceModeTable, button.modeValue),
        });
    };
    var contentData = useContentRows(resourceFields);
    var columns = [
        {
            name: formatMessage({
                id: "ui-br-agency.title.supportingResources.type.label",
            }),
            cellRenderer: function (row) { var _a; return ((_a = row.type) === null || _a === void 0 ? void 0 : _a.description) || "-"; },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.title.supportingResources.mode.label",
            }),
            cellRenderer: function (row) { var _a; return ((_a = row.mode) === null || _a === void 0 ? void 0 : _a.description) || "-"; },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.title.supportingResources.link.label",
            }),
            cellRenderer: function (row) {
                if (!row.link || row.link === "-")
                    return "-";
                return (React.createElement(TextLink, { href: row.link, target: "_blank", rel: "noopener noreferrer" }, row.link));
            },
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 }, resourceFields.length > 0 ? (React.createElement(EntityRowList, { data: contentData, columns: columns, onEdit: function (row) {
                    return openEditor(getValues("supportingResources.".concat(row._index)), row._index);
                }, onDelete: function (row) { return removeResource(row._index); } })) : (React.createElement("div", { className: css.emptyState },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.resources.empty" }))))),
        errorMessages.length > 0 && (React.createElement(Row, { "data-error-field": "supportingResources" },
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "error", className: css.tableErrorBanner }, errorMessages.map(function (msg) { return (React.createElement("div", { key: msg }, msg)); }))))),
        React.createElement(ResourceFormModal, { open: isOpen, isEditing: isEditing, draft: draft, onDraftChange: setDraft, resourceTypeOptions: (_a = resourceTypeTable === null || resourceTypeTable === void 0 ? void 0 : resourceTypeTable.onixCodetableItems) !== null && _a !== void 0 ? _a : [], resourceTypeLoading: resourceTypeLoading, resourceTypeStatus: resourceTypeStatus, resourceModeOptions: (_b = resourceModeTable === null || resourceModeTable === void 0 ? void 0 : resourceModeTable.onixCodetableItems) !== null && _b !== void 0 ? _b : [], resourceModeLoading: resourceModeLoading, resourceModeStatus: resourceModeStatus, addTitleId: addTitleId, onSave: saveItem, onClose: closeEditor }),
        React.createElement(Row, { className: css.actions },
            React.createElement(Col, { xs: 12 }, ADD_BUTTONS.map(function (button) { return (React.createElement(Button, { key: button.labelId, buttonStyle: "primary", disabled: !isAddReady(button), onClick: function () { return openAdd(button); } },
                React.createElement(FormattedMessage, { id: button.labelId }))); })))));
};
export default TitleSupportingResourcesForm;
